import { jsPDF } from 'jspdf'

const PAGE_MARGIN = 56
const HEADER_BAND_HEIGHT = 92
const META_PANEL_HEIGHT = 54
const CONTINUATION_TOP = 76
const FOOTER_SPACE = 56
const LINE_HEIGHT = 14
const TITLE_SIZE = 18
const SECTION_SIZE = 12
const SUBSECTION_SIZE = 10.5
const MINOR_SIZE = 9.5
const BODY_SIZE = 9.5
const LIST_INDENT = 20
const CELL_PAD_X = 8
const CELL_PAD_Y = 6
const MIN_COLUMN_WIDTH = 56

const INK = [28, 35, 48]
const MUTED = [113, 124, 141]
const ACCENT = [21, 58, 122]
const ACCENT_SOFT = [58, 88, 148]
const HAIRLINE = [223, 228, 236]
const PANEL = [246, 248, 251]
const ON_ACCENT = [255, 255, 255]
const ON_ACCENT_SOFT = [198, 212, 236]

const sanitize = (value) => (typeof value === 'string' ? value : value == null ? '' : String(value))

// Splits the assistant answer into structured sections so the PDF follows the
// relatorio_coaf.docx layout (numbered headings + free-text body).
const isNumberedLine = (line) => /^\s*\d+\s*[.)]\s+\S/.test(line)
const isMarkdownHeading = (line) => /^#{1,6}\s+\S/.test(line)
const isBulletItem = (line) => /^\s*[-*+•]\s+\S/.test(line)
const isOrderedItem = (line) => /^\s{2,}\d+[.)]\s+\S/.test(line)
const isRule = (line) => /^\s*([-*_])\1{2,}\s*$/.test(line)
const isTableSeparator = (line) => /^\s*\|?[\s:-]*-{2,}[\s|:-]*\|?\s*$/.test(line) && line.includes('-')
const isTableRow = (line) => /^\s*\|.*\|\s*$/.test(line)

const headingLevel = (line) => {
  const hashes = line.match(/^#{1,6}/)
  if (hashes) {
    return Math.min(hashes[0].length, 3)
  }
  return 1
}

const listMarker = (line) => {
  const ordered = line.match(/^\s*(\d+)[.)]\s+/)
  return ordered ? `${ordered[1]}.` : '•'
}

const stripListMarker = (line) => line.replace(/^\s*[-*+•]\s+/, '').replace(/^\s*\d+[.)]\s+/, '')

const tableRowToCells = (line) => line
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((cell) => cell.trim())

// An unindented "1. ..." line is ambiguous: it is a section heading in the plain
// SISCOAF layout ("1. Dados da Instituicao Comunicante") but an ordered list item
// when the assistant enumerates values ("1. Lancamento 1 - R$ 162.440,00"). Two
// context signals resolve it, both evaluated over the whole document:
//   - the report already marks its sections with markdown '#' headings, so any
//     numbered line is enumerating content rather than opening a section;
//   - the line sits in a tight run of numbered lines (no blank line between), the
//     shape of a list. Numbered headings are separated by their section body.
const buildNumberedHeadingSet = (lines) => {
  const numbered = lines.map(isNumberedLine)
  const headings = new Set()

  if (lines.some(isMarkdownHeading)) {
    return headings
  }

  numbered.forEach((isNumbered, index) => {
    if (!isNumbered || isOrderedItem(lines[index])) {
      return
    }
    const inTightRun = numbered[index - 1] === true || numbered[index + 1] === true
    if (!inTightRun && lines[index].trim().length < 120) {
      headings.add(index)
    }
  })

  return headings
}

// The assistant hard-wraps its prose, but a fixed wrap at the source width leaves
// every paragraph ragged once it is re-typeset at the PDF column width. Adjacent
// text lines are joined back into one paragraph so the renderer can reflow them.
const mergeParagraphs = (blocks) => blocks.reduce((merged, block) => {
  const previous = merged[merged.length - 1]
  if (block.type === 'text' && previous && previous.type === 'text') {
    previous.text = `${previous.text} ${block.text}`
    return merged
  }
  merged.push(block.type === 'text' ? { ...block } : block)
  return merged
}, [])

const parseAssistantReport = (rawText) => {
  const lines = sanitize(rawText).replace(/\r\n/g, '\n').split('\n')
  const numberedHeadings = buildNumberedHeadingSet(lines)
  const blocks = []
  let inCodeFence = false
  let table = null

  // Table rows are accumulated so the renderer can lay out real columns instead
  // of flattening every row into one line of text.
  const flushTable = () => {
    if (table && table.rows.length > 0) {
      blocks.push(table)
    }
    table = null
  }

  lines.forEach((rawLine, lineIndex) => {
    const line = rawLine.replace(/\s+$/g, '')

    // Fenced code blocks are formatting noise in a COAF report: keep the content,
    // drop the fences so the text is not rendered as a literal ``` marker.
    if (/^\s*```/.test(line)) {
      flushTable()
      inCodeFence = !inCodeFence
      return
    }

    // Checked before isTableRow: a "| --- | --- |" separator matches both.
    if (!inCodeFence && isTableSeparator(line)) {
      if (table && table.rows.length === 1) {
        table.headerRow = true
      }
      return
    }

    if (!inCodeFence && isTableRow(line)) {
      if (!table) {
        table = { type: 'table', rows: [], headerRow: false }
      }
      table.rows.push(tableRowToCells(line))
      return
    }

    flushTable()

    if (!line.trim()) {
      blocks.push({ type: 'space' })
      return
    }

    if (!inCodeFence && isRule(line)) {
      blocks.push({ type: 'rule' })
      return
    }

    if (!inCodeFence && isMarkdownHeading(line)) {
      blocks.push({ type: 'heading', level: headingLevel(line), text: line.replace(/^#{1,6}\s+/, '').trim() })
      return
    }

    if (!inCodeFence && numberedHeadings.has(lineIndex)) {
      blocks.push({ type: 'heading', level: 1, text: line.trim() })
      return
    }

    if (!inCodeFence && (isBulletItem(line) || isOrderedItem(line) || isNumberedLine(line))) {
      blocks.push({ type: 'list', text: stripListMarker(line), marker: listMarker(line) })
      return
    }

    blocks.push({ type: 'text', text: line.trim(), indent: 0 })
  })

  flushTable()

  return mergeParagraphs(blocks)
}

// Breaks a line into styled runs so **bold**, *italic* and `code` are rendered
// with the actual font style instead of leaking the markdown markers.
// Single underscores are intentionally NOT treated as italic: identifiers such as
// operation_id appear throughout the report payload and must stay literal.
const INLINE_PATTERN = /(\*\*\*[^*]+\*\*\*|___[^_]+___|\*\*[^*]+\*\*|__[^_]+__|\*[^*\n]+\*|`[^`\n]+`)/g

export const parseInlineRuns = (text) => {
  const runs = []
  const source = sanitize(text)
  let lastIndex = 0

  source.replace(INLINE_PATTERN, (match, _group, offset) => {
    if (offset > lastIndex) {
      runs.push({ text: source.slice(lastIndex, offset), bold: false, italic: false })
    }

    if (match.startsWith('***') || match.startsWith('___')) {
      runs.push({ text: match.slice(3, -3), bold: true, italic: true })
    } else if (match.startsWith('**') || match.startsWith('__')) {
      runs.push({ text: match.slice(2, -2), bold: true, italic: false })
    } else if (match.startsWith('`')) {
      runs.push({ text: match.slice(1, -1), bold: false, italic: false })
    } else {
      runs.push({ text: match.slice(1, -1), bold: false, italic: true })
    }

    lastIndex = offset + match.length
    return match
  })

  if (lastIndex < source.length) {
    runs.push({ text: source.slice(lastIndex), bold: false, italic: false })
  }

  return runs.filter((run) => run.text !== '')
}

const fontStyle = (run, baseBold, baseItalic) => {
  const bold = run.bold || baseBold
  const italic = run.italic || baseItalic
  if (bold && italic) return 'bolditalic'
  if (bold) return 'bold'
  if (italic) return 'italic'
  return 'normal'
}

export const buildCoafReportPdf = ({ operationId, generatedAt, reportText, metadata = {} }) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const contentWidth = pageWidth - PAGE_MARGIN * 2
  let cursorY = PAGE_MARGIN

  const setFill = (color) => doc.setFillColor(color[0], color[1], color[2])
  const setStroke = (color) => doc.setDrawColor(color[0], color[1], color[2])
  const setInk = (color) => doc.setTextColor(color[0], color[1], color[2])

  const newPage = () => {
    doc.addPage()
    cursorY = CONTINUATION_TOP
  }

  const ensureSpace = (needed) => {
    if (cursorY + needed > pageHeight - FOOTER_SPACE) {
      newPage()
    }
  }

  // Measures every token with its own font so mixed bold/italic lines break at the
  // right place. Whitespace is kept as its own token to preserve inter-word gaps.
  const buildTokens = (runs, baseBold, baseItalic, size) => {
    doc.setFontSize(size)
    const tokens = []
    runs.forEach((run) => {
      const style = fontStyle(run, baseBold, baseItalic)
      run.text.split(/(\s+)/).forEach((piece) => {
        if (piece !== '') {
          doc.setFont('helvetica', style)
          tokens.push({ text: piece, style, space: /^\s+$/.test(piece), width: doc.getTextWidth(piece) })
        }
      })
    })
    return tokens
  }

  const wrapTokens = (tokens, maxWidth) => {
    const lines = []
    let line = []
    let lineWidth = 0

    const flush = () => {
      while (line.length > 0 && line[line.length - 1].space) {
        line.pop()
      }
      if (line.length > 0) {
        lines.push(line)
      }
      line = []
      lineWidth = 0
    }

    tokens.forEach((token) => {
      if (line.length === 0 && token.space) {
        return
      }
      if (lineWidth + token.width > maxWidth && line.length > 0) {
        flush()
        if (token.space) {
          return
        }
      }
      line.push(token)
      lineWidth += token.width
    })

    flush()
    return lines
  }

  const drawTokens = (tokens, x, y, size, color) => {
    doc.setFontSize(size)
    setInk(color)
    let cursorX = x
    tokens.forEach((token) => {
      doc.setFont('helvetica', token.style)
      doc.text(token.text, cursorX, y)
      cursorX += token.width
    })
  }

  const writeRuns = (runs, {
    size,
    baseBold = false,
    baseItalic = false,
    color = INK,
    indent = 0,
    gap = 0,
    lineHeight = LINE_HEIGHT,
    marker = null,
  }) => {
    const lines = wrapTokens(buildTokens(runs, baseBold, baseItalic, size), contentWidth - indent)

    lines.forEach((lineTokens, index) => {
      ensureSpace(lineHeight)
      if (index === 0 && marker) {
        marker(PAGE_MARGIN + indent, cursorY)
      }
      drawTokens(lineTokens, PAGE_MARGIN + indent, cursorY, size, color)
      cursorY += lineHeight
    })

    cursorY += gap
  }

  const hairline = (y, color = HAIRLINE) => {
    setStroke(color)
    doc.setLineWidth(0.6)
    doc.line(PAGE_MARGIN, y, pageWidth - PAGE_MARGIN, y)
  }

  // Columns are sized from their natural content width, then scaled to fill the
  // text column. Narrow columns are clamped first so a one-word column such as
  // "CNPJ" cannot collapse to an unreadable sliver.
  const columnWidths = (rows, columnCount) => {
    doc.setFontSize(BODY_SIZE)
    const natural = Array.from({ length: columnCount }, (_, column) => {
      const widest = rows.reduce((max, row) => {
        doc.setFont('helvetica', 'bold')
        return Math.max(max, doc.getTextWidth(sanitize(row[column]).replace(/[*`_]/g, '')))
      }, 0)
      return widest + CELL_PAD_X * 2
    })

    const total = natural.reduce((sum, width) => sum + width, 0) || 1
    const scaled = natural.map((width) => (width / total) * contentWidth)
    const clamped = scaled.map((width) => Math.max(width, Math.min(MIN_COLUMN_WIDTH, contentWidth / columnCount)))
    const clampedTotal = clamped.reduce((sum, width) => sum + width, 0)

    return clamped.map((width) => (width / clampedTotal) * contentWidth)
  }

  const drawTable = (block) => {
    const columnCount = block.rows.reduce((max, row) => Math.max(max, row.length), 0)
    if (columnCount === 0) {
      return
    }

    const widths = columnWidths(block.rows, columnCount)
    const offsets = widths.reduce((acc, width, index) => {
      acc.push(index === 0 ? PAGE_MARGIN : acc[index - 1] + widths[index - 1])
      return acc
    }, [])

    const layoutRow = (row, isHeader) => row.map((cell, column) => wrapTokens(
      buildTokens(parseInlineRuns(cell), isHeader, false, BODY_SIZE),
      widths[column] - CELL_PAD_X * 2,
    ))

    const drawRow = (row, isHeader) => {
      const cellLines = layoutRow(row, isHeader)
      const lineCount = cellLines.reduce((max, lines) => Math.max(max, lines.length), 1)
      const rowHeight = lineCount * LINE_HEIGHT + CELL_PAD_Y * 2 - (LINE_HEIGHT - BODY_SIZE)

      ensureSpace(rowHeight)

      if (isHeader) {
        setFill(ACCENT)
        doc.rect(PAGE_MARGIN, cursorY, contentWidth, rowHeight, 'F')
      }

      cellLines.forEach((lines, column) => {
        lines.forEach((lineTokens, index) => {
          drawTokens(
            lineTokens,
            offsets[column] + CELL_PAD_X,
            cursorY + CELL_PAD_Y + BODY_SIZE + index * LINE_HEIGHT,
            BODY_SIZE,
            isHeader ? ON_ACCENT : INK,
          )
        })
      })

      cursorY += rowHeight

      if (!isHeader) {
        hairline(cursorY)
      }
    }

    cursorY += 6
    ensureSpace(LINE_HEIGHT * 2)

    block.rows.forEach((row, index) => {
      drawRow(row, block.headerRow && index === 0)
    })

    cursorY += 12
  }

  // Cover band: title, scope and a confidentiality tag, so the first page reads as
  // an official communication rather than a wall of text.
  setFill(ACCENT)
  doc.rect(0, 0, pageWidth, HEADER_BAND_HEIGHT, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(TITLE_SIZE)
  setInk(ON_ACCENT)
  doc.text('Relatorio de Comunicacao ao COAF', PAGE_MARGIN, 46)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(MINOR_SIZE)
  setInk(ON_ACCENT_SOFT)
  doc.text('Comunicacao de Operacao Suspeita (COS) - Espelho de Dados SISCOAF', PAGE_MARGIN, 66)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  setInk(ON_ACCENT_SOFT)
  doc.text('CONFIDENCIAL', pageWidth - PAGE_MARGIN, 46, { align: 'right' })

  // Metadata strip: the identifying fields a reviewer looks for first.
  setFill(PANEL)
  doc.rect(0, HEADER_BAND_HEIGHT, pageWidth, META_PANEL_HEIGHT, 'F')
  setStroke(HAIRLINE)
  doc.setLineWidth(0.6)
  doc.line(0, HEADER_BAND_HEIGHT + META_PANEL_HEIGHT, pageWidth, HEADER_BAND_HEIGHT + META_PANEL_HEIGHT)

  const metaFields = [
    ['Operacao', operationId],
    ['Emitido em', generatedAt],
    ['Instituicao Comunicante', metadata.institution],
  ].filter(([, value]) => sanitize(value).trim() !== '')

  if (metaFields.length > 0) {
    const fieldWidth = contentWidth / metaFields.length
    metaFields.forEach(([label, value], index) => {
      const x = PAGE_MARGIN + fieldWidth * index
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(7)
      setInk(MUTED)
      doc.text(label.toUpperCase(), x, HEADER_BAND_HEIGHT + 21)

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(MINOR_SIZE)
      setInk(INK)
      doc.text(sanitize(value), x, HEADER_BAND_HEIGHT + 37)
    })
  }

  cursorY = HEADER_BAND_HEIGHT + META_PANEL_HEIGHT + 34

  // Body (generated by the AI assistant)
  const blocks = parseAssistantReport(reportText)
  blocks.forEach((block) => {
    if (block.type === 'space') {
      cursorY += LINE_HEIGHT / 2
      return
    }

    if (block.type === 'rule') {
      ensureSpace(16)
      hairline(cursorY)
      cursorY += 14
      return
    }

    if (block.type === 'table') {
      drawTable(block)
      return
    }

    if (block.type === 'heading') {
      // A section heading and its underline must not be orphaned at a page foot.
      ensureSpace(LINE_HEIGHT * 3)
      cursorY += block.level === 1 ? 14 : 10

      writeRuns(parseInlineRuns(block.text), {
        size: block.level === 1 ? SECTION_SIZE : SUBSECTION_SIZE,
        baseBold: true,
        color: block.level === 1 ? ACCENT : ACCENT_SOFT,
        lineHeight: block.level === 1 ? LINE_HEIGHT + 2 : LINE_HEIGHT,
      })

      if (block.level === 1) {
        hairline(cursorY - 2, ACCENT)
        cursorY += 10
      } else {
        cursorY += 4
      }
      return
    }

    if (block.type === 'list') {
      writeRuns(parseInlineRuns(block.text), {
        size: BODY_SIZE,
        indent: LIST_INDENT,
        gap: 1,
        marker: (x, y) => {
          if (block.marker === '•') {
            setFill(ACCENT_SOFT)
            doc.circle(x - LIST_INDENT + 7, y - 3, 1.7, 'F')
            return
          }
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(BODY_SIZE)
          setInk(ACCENT_SOFT)
          doc.text(block.marker, x - LIST_INDENT + 2, y)
        },
      })
      return
    }

    writeRuns(parseInlineRuns(block.text), { size: BODY_SIZE })
  })

  // Running header (from page 2) and confidentiality footer on every page.
  const pageCount = doc.internal.getNumberOfPages()
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page)

    if (page > 1) {
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(7.5)
      setInk(MUTED)
      doc.text('RELATORIO DE COMUNICACAO AO COAF', PAGE_MARGIN, 44)
      if (sanitize(operationId).trim() !== '') {
        doc.setFont('helvetica', 'normal')
        doc.text(sanitize(operationId), pageWidth - PAGE_MARGIN, 44, { align: 'right' })
      }
      hairline(52)
    }

    hairline(pageHeight - 40)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    setInk(MUTED)
    doc.text(
      'SIGILO - Lei no 9.613/1998. Documento confidencial. O envolvido nao pode ser cientificado.',
      PAGE_MARGIN,
      pageHeight - 26,
    )
    doc.text(`Pagina ${page} de ${pageCount}`, pageWidth - PAGE_MARGIN, pageHeight - 26, { align: 'right' })
  }

  return doc
}

export const downloadCoafReportPdf = (options) => {
  const doc = buildCoafReportPdf(options)
  const safeId = sanitize(options.operationId).replace(/[^\w-]+/g, '_') || 'operacao'
  doc.save(`relatorio_coaf_${safeId}.pdf`)
}
